import React, { Fragment, useState } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import SEO from "../../components/ceo/CEO";
import useAdd from "../../api/useAdd";

const incidentTypes = [
  "Sexual assault",
  "Forced labor",
  "Suicide",
  "Child marriage",
  "Honor killing",
  "Domestic violence",
  "Prevention of education or work",
  "Economic issues",
];

const genderTypes = ["Male", "Female"];

const ReportViolationsForm = () => {
  const [formData, setFormData] = useState({
    incident_type: "",
    victim_name: "",
    victim_age: "",
    victim_gender: "",
    victim_connections: "",
    incident_date: "",
    incident_location: "",
    witnesses: "",
    authority_response: "",
    incident_details: "",
    supporting_document: null,
    phone_number: "", // Added field
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, supporting_document: e.target.files[0] });
  };

  const { handleAdd, loading } = useAdd(
    "incidents",
    null,
    "Your request was successfully submitted",
    "Operation failed"
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    // Required fields
    const requiredFields = [
      "incident_type",
      "victim_name",
      "victim_age",
      "victim_gender",
      "incident_date",
      "incident_location",
      "incident_details",
      "phone_number", // Added to required fields
    ];

    // Check if all required fields are filled
    let formErrors = {};

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        formErrors[field] = `${field.replace("_", " ")} is required`;
      }
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Clear errors if valid
    setErrors({});

    // Create FormData for submitting the form
    const formDataToSend = new FormData();
    for (const key in formData) {
      if (formData[key] !== null && formData[key] !== "") {
        formDataToSend.append(key, formData[key]);
      }
    }

    if (formData.supporting_document) {
      formDataToSend.append(
        "supporting_document",
        formData.supporting_document
      );
    }

    handleAdd(formDataToSend);
  };

  return (
    <Fragment>
      <SEO
        title="Report Violations"
        description="Report human rights violations and violence. Help us seek justice by filling out the form with the details of the incident."
        url="https://shallafg.org/report-violations/"
      />
      <Box
        sx={{
          background: "linear-gradient(180deg, #0A1128 0%, #1A2E55 100%)",
          color: "#FFFFFF",
          paddingY: "60px",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", marginBottom: "40px" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "2px",
                marginBottom: "20px",
              }}
            >
              Raise Your Voice
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#D1D5DB",
                maxWidth: "800px",
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              Demand Justice Against Violence and Human Rights Violations. If
              you or someone you know has been a victim of injustice or
              violence, we are here to support you. Please fill out the form
              below to report the incident and help us seek justice for you.
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel
                    sx={{
                      color: "#A9C9EA",
                      "&.Mui-focused": {
                        color: "#A9C9EA",
                      },
                    }}
                  >
                    Type of Incident
                  </InputLabel>
                  <Select
                    label="Type of Incident"
                    name="incident_type"
                    value={formData.incident_type}
                    onChange={handleChange}
                    sx={{
                      backgroundColor: "#1A2E55",
                      color: "#A9C9EA",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#A9C9EA",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#A9C9EA",
                      },
                      "& .MuiSelect-icon": {
                        color: "#A9C9EA",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: "#1A2E55",
                          color: "#A9C9EA",
                        },
                      },
                    }}
                  >
                    {incidentTypes.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{
                          backgroundColor: "#1A2E55",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "#007BFF",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#007BFF",
                            "&:hover": {
                              backgroundColor: "#0056b3",
                            },
                          },
                        }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.incident_type && (
                    <Typography color="error">
                      {errors.incident_type}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Victim's Name"
                  name="victim_name"
                  fullWidth
                  variant="outlined"
                  value={formData.victim_name}
                  onChange={handleChange}
                  error={!!errors.victim_name}
                  helperText={errors.victim_name}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Victim's Age"
                  name="victim_age"
                  fullWidth
                  variant="outlined"
                  value={formData.victim_age}
                  onChange={handleChange}
                  error={!!errors.victim_age}
                  helperText={errors.victim_age}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ color: "#FFFFFF", mb: 1 }}>Gender</Typography>
                <RadioGroup
                  row
                  name="victim_gender"
                  value={formData.victim_gender}
                  onChange={handleChange}
                >
                  {genderTypes.map((gender) => (
                    <FormControlLabel
                      key={gender}
                      value={gender}
                      control={<Radio sx={{ color: "red" }} />}
                      label={gender}
                    />
                  ))}
                </RadioGroup>
                {errors.victim_gender && (
                  <Typography color="error">{errors.victim_gender}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Phone Number" // Added field
                  name="phone_number"
                  fullWidth
                  variant="outlined"
                  value={formData.phone_number}
                  onChange={handleChange}
                  error={!!errors.phone_number}
                  helperText={errors.phone_number}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Family or Social Connections"
                  name="victim_connections"
                  fullWidth
                  variant="outlined"
                  value={formData.victim_connections}
                  onChange={handleChange}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Date and Time of the Incident"
                  name="incident_date"
                  type="datetime-local"
                  fullWidth
                  variant="outlined"
                  value={formData.incident_date}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  error={!!errors.incident_date}
                  helperText={errors.incident_date}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                    "& input::-webkit-calendar-picker-indicator": {
                      filter: "invert(1)",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Location of the Incident"
                  name="incident_location"
                  fullWidth
                  variant="outlined"
                  value={formData.incident_location}
                  onChange={handleChange}
                  error={!!errors.incident_location}
                  helperText={errors.incident_location}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Witnesses or Other Informants"
                  name="witnesses"
                  fullWidth
                  variant="outlined"
                  value={formData.witnesses}
                  onChange={handleChange}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Response of Local Authorities"
                  name="authority_response"
                  fullWidth
                  variant="outlined"
                  value={formData.authority_response}
                  onChange={handleChange}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Details of the Incident"
                  name="incident_details"
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={formData.incident_details}
                  onChange={handleChange}
                  error={!!errors.incident_details}
                  helperText={errors.incident_details}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ color: "#A9C9EA" }}>
                  Supporting Document (Optional)
                </Typography>
                <TextField
                  type="file"
                  name="supporting_document"
                  fullWidth
                  variant="outlined"
                  inputProps={{
                    accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp",
                  }}
                  onChange={handleFileChange}
                  sx={{
                    backgroundColor: "#1A2E55",
                    borderRadius: "8px",
                    "& .MuiInputLabel-root": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#A9C9EA",
                      },
                      "&:hover fieldset": {
                        borderColor: "#007BFF",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#007BFF",
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={24} />}
                  sx={{
                    backgroundColor: "#10a7e0",
                    color: "#FFFFFF",
                    padding: "14px",
                    fontSize: "1rem",
                    borderRadius: "30px",
                    "&:hover": {
                      backgroundColor: "#10a7e0",
                    },
                  }}
                >
                  {loading ? "loading..." : "Submit Report"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Container>
      </Box>
    </Fragment>
  );
};

export default ReportViolationsForm;
